import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InfoHintComponent } from '../../components/shared/info-hint.component';
import { PersonLinkComponent } from '../../components/shared/person-link.component';
import { EventLinkComponent } from '../../components/shared/event-link.component';
import { UsersService } from '../../services/users-service.service';
import { ClaimFunnelRowDto } from '../../data-transfer-objects/claim-funnel-row.dto';

/**
 * Where every invited organizer got to.
 *
 * The funnel has three observable stages and deliberately not four. The claim
 * link goes straight to the page, so "clicked the email" and "opened the page"
 * are one event - listing both would invent a step and then have to fake its
 * numbers. What is real: invited, opened, submitted step 1, claimed.
 *
 * Rows with no opens are kept and sorted where they can be seen, because an
 * event nobody opened and an event opened six times and abandoned are opposite
 * problems - the first is an email problem, the second is a page problem - and
 * a report that hides the first answers neither.
 *
 * People and machines are counted in separate columns rather than the machines
 * being dropped. They are not the same fact and neither one is noise: a link
 * checker opening the page is proof the mail was delivered and the link works,
 * which is worth knowing on a row where no person ever showed up. What it is
 * not is interest, so it does not get to sit in the column that means interest.
 */
@Component({
  selector: 'app-claim-funnel',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    InfoHintComponent,
    PersonLinkComponent,
    EventLinkComponent,
  ],
  templateUrl: './claim-funnel.component.html',
  styleUrls: ['./claim-funnel.component.scss'],
})
export class ClaimFunnelComponent implements OnInit {
  rows: ClaimFunnelRowDto[] = [];
  loading = true;
  error: string | null = null;
  /*
   * The one action awaiting confirmation, if any.
   *
   * A single field rather than one per action: two rows - or two buttons on
   * the same row - cannot be half-confirmed at once, and asking twice about
   * different things simultaneously is how the wrong one gets pressed.
   */
  pending: { id: string; action: 'unclaim' | 'clear' } | null = null;
  busyId: string | null = null;
  notice: string | null = null;

  constructor(private usersService: UsersService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.usersService.getClaimFunnel().subscribe({
      next: (rows) => {
        this.rows = rows;
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load the funnel: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  get invited(): number { return this.rows.length; }
  get openedCount(): number { return this.rows.filter((r) => r.opened > 0).length; }
  /** Events only a machine ever reached - delivered, but nobody looked. */
  get machineOnlyCount(): number {
    return this.rows.filter((r) => r.opened === 0 && r.automatedOpens > 0).length;
  }
  get submittedCount(): number { return this.rows.filter((r) => r.submitted > 0).length; }
  get claimedCount(): number { return this.rows.filter((r) => !!r.ownerId).length; }

  /*
   * How long the invitation has been out. Cold outreach settles within a few
   * days, so age is what turns "no reply yet" into "this one is finished" -
   * and a date alone makes the reader do that arithmetic on every row.
   */
  daysSinceSent(r: ClaimFunnelRowDto): string {
    if (!r.sentAt) return '';
    const days = Math.floor((Date.now() - new Date(r.sentAt).getTime()) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days} days ago`;
  }

  /*
   * Three tones, not nine. What matters at a glance is whether the mail failed,
   * arrived, or is still in flight - the exact wording stays in the cell for
   * anyone who wants it. "Engaged" is deliberately not a success colour: an
   * open is a pixel an image proxy fetches, and a click can be a link checker,
   * so colouring those green would overstate them next to the People column.
   */
  deliveryTone(r: ClaimFunnelRowDto): 'failed' | 'engaged' | 'landed' | 'pending' {
    switch (r.delivery) {
      case 'bounced':
      case 'complained':
      case 'failed':
        return 'failed';
      case 'opened':
      case 'clicked':
        return 'engaged';
      case 'delivered':
        return 'landed';
      default:
        return 'pending';
    }
  }

  deliveryTitle(r: ClaimFunnelRowDto): string {
    switch (r.delivery) {
      case 'bounced':
        return 'The address rejected it. Fix or drop it before the next batch — bounces cost sending reputation.';
      case 'complained':
        return 'Marked as spam by the recipient. Do not email this address again.';
      case 'opened':
        return 'A tracking pixel loaded. That is often the person, but image proxies fetch it too, so it is not proof.';
      case 'clicked':
        return 'The tracked link was fetched. A mail-security scanner does this without a person ever seeing the page — check the People column.';
      case 'delivered':
        return 'Accepted by the receiving server. Nothing has happened since.';
      default:
        return 'Still in flight at Resend.';
    }
  }

  /** Furthest stage reached, which is what the row should be read as. */
  stage(r: ClaimFunnelRowDto): 'claimed' | 'submitted' | 'opened' | 'invited' {
    if (r.ownerId) return 'claimed';
    if (r.submitted > 0) return 'submitted';
    if (r.opened > 0) return 'opened';
    return 'invited';
  }

  stageLabel(r: ClaimFunnelRowDto): string {
    switch (this.stage(r)) {
      case 'claimed': return 'Claimed';
      case 'submitted': return 'Started, not finished';
      case 'opened': return 'Opened only';
      default: return 'Not opened';
    }
  }

  /*
   * Two presses, not a dialog. Un-claiming is reversible - the organizer can
   * claim again from the same link - but it silently removes someone's
   * ownership, so it should not be one stray click away.
   */
  isPending(r: ClaimFunnelRowDto, action: 'unclaim' | 'clear'): boolean {
    return this.pending?.id === r.eventId && this.pending.action === action;
  }

  ask(r: ClaimFunnelRowDto, action: 'unclaim' | 'clear'): void {
    this.notice = null;
    this.pending = this.isPending(r, action) ? null : { id: r.eventId, action };
  }

  cancel(): void {
    this.pending = null;
  }

  confirmUnclaim(r: ClaimFunnelRowDto): void {
    this.busyId = r.eventId;
    this.usersService.unclaimEvent(r.eventId).subscribe({
      next: () => {
        this.busyId = null;
        this.pending = null;
        this.notice = `"${r.title}" is unclaimed again and its link works for the next person who opens it.`;
        this.load();
      },
      error: (err) => {
        this.busyId = null;
        this.pending = null;
        this.error =
          err?.status === 409
            ? `"${r.title}" is not currently claimed.`
            : `Could not unclaim "${r.title}": ${err?.message ?? err}`;
      },
    });
  }

  /** True once there is anything to clear - no point offering an empty wipe. */
  hasStats(r: ClaimFunnelRowDto): boolean {
    return r.opened > 0 || r.submitted > 0 || r.automatedOpens > 0;
  }

  confirmClear(r: ClaimFunnelRowDto): void {
    this.busyId = r.eventId;
    this.usersService.clearEventFunnel(r.eventId).subscribe({
      next: () => {
        this.busyId = null;
        this.pending = null;
        /* Says what survived as well as what went: the row stays claimed if it
           was, and that is the part a reader is most likely to assume wrong. */
        this.notice = `"${r.title}" is back to nothing measured. Its claim, if it had one, is untouched.`;
        this.load();
      },
      error: (err) => {
        this.busyId = null;
        this.pending = null;
        this.error = `Could not clear "${r.title}": ${err?.message ?? err}`;
      },
    });
  }
}
